import { configStore } from 'config/configStore';
import { CalciumAnalysisResult, LesionInfo } from '@models/Parameter';
import { mat3, vec3 } from 'gl-matrix';
import {
  decompress,
  isCompressed,
  readHeader,
  readImage,
} from 'nifti-reader-js';

import simplify from 'simplify-js';

function isPointInPolygon(
  point: [number, number],
  polygon: [number, number][],
): boolean {
  let [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-10) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function findConnectedComponents(
  imageData: Uint8Array,
  width: number,
  height: number,
  // labelSet: Set<number>,
  labelSet: Uint8Array,
): number[][] {
  function ufMake(n: number) {
    const parent = new Int32Array(n);
    for (let i = 0; i < n; ++i) parent[i] = i;
    const find = (x: number): number => {
      let r = x;
      while (parent[r] !== r) r = parent[r];
      let t: number;
      while (parent[x] !== r) {
        t = parent[x];
        parent[x] = r;
        x = t;
      }
      return r;
    };
    const unify = (a: number, b: number) => {
      let ra = find(a),
        rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    };
    return { parent, find, unify };
  }

  // 한 프레임의 전체 run을 저장(픽셀 수에 비해 훨씬 작음)
  const runsStart: number[] = [];
  const runsEnd: number[] = [];
  const runsY: number[] = [];
  const runsLab: number[] = [];

  // 1패스: RLE 추출 + 위줄과의 겹침으로 유니온
  let nextLabel = 0;
  const uf = ufMake(width * height); // 라벨 상한 넉넉히
  let idx = 0;

  // 이전 줄의 run 인덱스 목록(전역 runs의 인덱스들)
  let prevRowRunIdx: number[] = [];

  for (let y = 0; y < height; ++y) {
    const currRowRunIdx: number[] = [];

    // --- RLE 추출 ---
    let x = 0;
    while (x < width) {
      while (x < width && !labelSet[imageData[idx]]) {
        x++;
        idx++;
      }
      if (x >= width) break;
      const s = x;
      while (x < width && labelSet[imageData[idx]]) {
        x++;
        idx++;
      }
      const e = x - 1;

      const lab = nextLabel++;
      const runIdx = runsStart.length;
      runsStart.push(s);
      runsEnd.push(e);
      runsY.push(y);
      runsLab.push(lab);
      currRowRunIdx.push(runIdx);
    }

    // --- 위줄의 run들과 겹치면 union ---
    if (prevRowRunIdx.length && currRowRunIdx.length) {
      let i = 0,
        j = 0;
      while (i < prevRowRunIdx.length && j < currRowRunIdx.length) {
        const a = prevRowRunIdx[i];
        const b = currRowRunIdx[j];
        const aL = runsStart[a],
          aR = runsEnd[a];
        const bL = runsStart[b],
          bR = runsEnd[b];

        if (aR >= bL - 1 && bR >= aL - 1) {
          // 겹치는 모든 b에 대해 union
          let jj = j;
          while (jj < currRowRunIdx.length) {
            const bb = currRowRunIdx[jj];
            const bL2 = runsStart[bb],
              bR2 = runsEnd[bb];
            if (aR >= bL2 - 1 && bR2 >= aL - 1) {
              uf.unify(runsLab[a], runsLab[bb]);
              jj++;
            } else break;
          }
          if (aR < runsEnd[b]) i++;
          else j++;
        } else if (aR < bL - 1) {
          i++;
        } else {
          j++;
        }
      }
    }

    prevRowRunIdx = currRowRunIdx;
  }

  // 2패스: 루트별로 seed 하나만 선택(각 run의 첫 픽셀 인덱스를 후보로)
  const rootToSeed = new Map<number, number>();
  for (let r = 0; r < runsStart.length; ++r) {
    const root = uf.find(runsLab[r]);
    const seedIdx = runsY[r] * width + runsStart[r]; // 해당 run의 첫 픽셀
    const prev = rootToSeed.get(root);
    if (prev === undefined || seedIdx < prev) rootToSeed.set(root, seedIdx);
  }

  // 출력: [[seed], [seed], ...]
  const out: number[][] = [];
  for (const seed of rootToSeed.values()) out.push([seed]);
  return out;
}

// calculate out line points
// 2차원 단면의 아웃라인 계산 함수
export function traceOutlinePoints(
  imageData: Uint8Array,
  width: number,
  height: number,
  labelSet: Uint8Array,
  start: number,
): [number, number][] {
  //외곽선 다 가져오는 코드
  const DIRECTION = [
    [-1, -1],
    [0, -1],
    [1, -1],
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
    [-1, 0],
  ];

  const outline: [number, number][] = [];
  if (!imageData || !labelSet) return outline;

  const getCoord = (idx: number): [number, number] => [
    idx % width,
    Math.floor(idx / width),
  ];

  let [startX, startY] = getCoord(start);
  let currX = startX;
  let currY = startY;
  let prevDir = 7;

  const sidx = currY * width + currX;
  if (
    imageData[sidx] < 0 ||
    imageData[sidx] > labelSet.length - 1 ||
    !labelSet[imageData[sidx]]
  ) {
    return outline;
  }

  outline.push([currX, currY]);

  let first = true;
  let steps = 0;
  const maxSteps = width * height * 8;

  while (true) {
    steps++;
    if (steps > maxSteps) {
      // 안전장치
      break;
    }

    let found = false;

    // Moore-Neighbor 방식: 이전 방향 기준으로 주변 8방향 스캔
    for (let i = 1; i <= 8; i++) {
      const dir = (prevDir + i) % 8;
      const [dx, dy] = DIRECTION[dir];
      const nx = currX + dx;
      const ny = currY + dy;

      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;

      const nidx = ny * width + nx;
      if (labelSet[imageData[nidx]]) {
        currX = nx;
        currY = ny;
        prevDir = (dir + 6) % 8; // 다음 스캔 시작 방향
        found = true;
        break;
      }
    }

    if (!found) break;
    if (!first && currX === startX && currY === startY) break;

    outline.push([currX, currY]);
    first = false;
  }

  // 마지막 점과 시작점이 다르면 닫아줌(선 그릴 때 자동으로 닫는다면 생략해도 됨)
  if (outline.length >= 2) {
    const [sx, sy] = outline[0];
    const [ex, ey] = outline[outline.length - 1];
    if (sx !== ex || sy !== ey) {
      outline.push([sx, sy]);
    }
  }

  return outline;

  // 꼭지점만 가져오는 코드
  // const dirs = [
  //   -width - 1,
  //   -1,
  //   width - 1,
  //   width,
  //   width + 1,
  //   1,
  //   1 - width,
  //   -width,
  // ];
  // let startIdx = -1;
  // for (let i = 0; i < 8; i++) {
  //   const ni = start + dirs[i];
  //   if (ni < 0 || ni >= width * height) continue;
  //   if (!labelSet[imageData[ni]]) {
  //     startIdx = i;
  //     break;
  //   }
  // }
  // if (startIdx === -1) return [];
  // const outline: [number, number][] = [];
  // let nextIdx = startIdx;
  // let next = start;
  // let breakCount = 0;
  // let bsidenum = -1;
  // const visited = new Set<number>();

  // const getCoord = (idx: number): [number, number] => [
  //   (idx % width) - Math.floor(width / 2),
  //   Math.floor(idx / width) - Math.floor(height / 2),
  //   idx % width,
  //   Math.floor(idx / width),
  // ];

  // outline.push(getCoord(start));
  // visited.add(start);

  // while (true) {
  //   if (nextIdx < 0) nextIdx += 8;
  //   if (nextIdx > 7) nextIdx = 0;

  //   const ni = next + dirs[nextIdx];
  //   if (ni < 0 || ni >= width * height) {
  //     breakCount++;
  //     nextIdx++;
  //     continue;
  //   }
  //   if (labelSet[imageData[ni]]) {
  //     if (ni === start) break;
  //     const [x, y] = getCoord(ni);

  //     if ([0, 2, 4, 6].includes(nextIdx)) {
  //       if (bsidenum === nextIdx && outline.length > 0) outline.pop();
  //     }
  //     bsidenum = nextIdx;

  //     let nH = 0,
  //       nV = 0;
  //     const gx = ni % width;
  //     const gy = Math.floor(ni / width);
  //     if (gx + 1 < width && labelSet[imageData[gy * width + gx + 1]]) nH++;
  //     if (gx - 1 >= 0 && labelSet[imageData[gy * width + gx - 1]]) nH++;
  //     if (gy + 1 < height && labelSet[imageData[(gy + 1) * width + gx]]) nV++;
  //     if (gy - 1 >= 0 && labelSet[imageData[(gy - 1) * width + gx]]) nV++;

  //     if (!(nH === 2 || nV === 2 || nH + nV === 0)) {
  //       if (!visited.has(ni)) {
  //         outline.push([x, y]);
  //         visited.add(ni);
  //       }
  //     }

  //     next = ni;
  //     nextIdx -= 4;
  //     breakCount = 0;
  //   } else {
  //     breakCount++;
  //   }

  //   nextIdx++;
  //   if (breakCount > 9) break;
  // }

  // if (outline.length >= 3) {
  //   const [sx, sy] = outline[0];
  //   const [ex, ey] = outline[outline.length - 1];
  //   if (sx !== ex || sy !== ey) {
  //     outline.push([sx, sy]);
  //   }
  // }

  // return outline;
}

export function simplifyOutline(
  outline: [number, number][],
  tolerance: number = 2, // 2픽셀 허용 오차, 필요시 조정
): [number, number][] {
  // simplify-js는 {x, y} 형태를 요구
  const pts = outline.map(([x, y]) => ({ x, y }));
  const simplified = simplify(pts, tolerance, true); // highQuality=true
  return simplified.map((p) => [p.x, p.y]);
}

// 메인 함수!
export function traceOutlinesWithCenterOption(
  imageData: Uint8Array,
  width: number,
  height: number,
  labelValue: number[],
  onlyCenterRegion: boolean = false,
): [number, number][][] {
  function makeLabelTable(labelArr: number[]): Uint8Array {
    const table = new Uint8Array(256); // Uint8Array는 0~255용
    for (const v of labelArr) table[v] = 1;
    return table;
  }

  const labelTable = makeLabelTable(labelValue);
  const components = findConnectedComponents(
    imageData,
    width,
    height,
    labelTable,
  );
  const outlines: [number, number][][] = [];
  for (const comp of components) {
    if (comp.length === 0) continue;
    const outline = traceOutlinePoints(
      imageData,
      width,
      height,
      labelTable,
      comp[0],
    );
    if (outline.length > 0) {
      const [x, y] = outline[0];
      outline.push([x, y]);
      //const simplifiedOutline = simplifyOutline(outline, 4);
      //outlines.push(simplifiedOutline);
      outlines.push(outline);
    }
  }

  if (!onlyCenterRegion) {
    return outlines;
  } else {
    // 중심 (label좌표)
    const centerX = Math.floor(width / 2);
    const centerY = Math.floor(height / 2);
    // 중심이 포함된 outline만 반환 (여러 개 있을 경우 가장 첫 번째 것만)
    for (const outline of outlines) {
      const polygon = outline.map(
        ([x, y]) => [x + centerX, y + centerY] as [number, number],
      );
      if (isPointInPolygon([centerX, centerY], polygon)) {
        return [outline];
      }
    }
    // 없으면 빈 배열
    return [];
  }
}

// 3차원 값 보정
function trilinear3d(
  view: Int16Array | number[],
  width: number,
  height: number,
  depth: number,
  index2: number, // z
  index1: number, // y
  index0: number, // x
): number {
  let blend0: number, index0_low: number, index0_high: number;
  if (index0 <= 0.5) {
    blend0 = 0.0;
    index0_low = 0;
    index0_high = 0;
  } else if (index0 >= width - 0.5) {
    blend0 = 0.0;
    index0_low = width - 1;
    index0_high = width - 1;
  } else {
    const t = index0 - 0.5;
    index0_low = Math.floor(t);
    blend0 = t - index0_low;
    index0_high = index0_low + 1;
  }

  let blend1: number, index1_low: number, index1_high: number;
  if (index1 <= 0.5) {
    blend1 = 0.0;
    index1_low = 0;
    index1_high = 0;
  } else if (index1 >= height - 0.5) {
    blend1 = 0.0;
    index1_low = height - 1;
    index1_high = height - 1;
  } else {
    const t = index1 - 0.5;
    index1_low = Math.floor(t);
    blend1 = t - index1_low;
    index1_high = index1_low + 1;
  }

  let blend2: number, index2_low: number, index2_high: number;
  if (index2 <= 0.5) {
    blend2 = 0.0;
    index2_low = 0;
    index2_high = 0;
  } else if (index2 >= depth - 0.5) {
    blend2 = 0.0;
    index2_low = depth - 1;
    index2_high = depth - 1;
  } else {
    const t = index2 - 0.5;
    index2_low = Math.floor(t);
    blend2 = t - index2_low;
    index2_high = index2_low + 1;
  }

  function at(z: number, y: number, x: number) {
    return view[z * width * height + y * width + x];
  }

  const v0 = at(index2_low, index1_low, index0_low);
  const v1 = at(index2_low, index1_low, index0_high);
  const v2 = at(index2_low, index1_high, index0_low);
  const v3 = at(index2_low, index1_high, index0_high);
  const v4 = at(index2_high, index1_low, index0_low);
  const v5 = at(index2_high, index1_low, index0_high);
  const v6 = at(index2_high, index1_high, index0_low);
  const v7 = at(index2_high, index1_high, index0_high);

  const v01 = v0 * (1 - blend0) + v1 * blend0;
  const v23 = v2 * (1 - blend0) + v3 * blend0;
  const v45 = v4 * (1 - blend0) + v5 * blend0;
  const v67 = v6 * (1 - blend0) + v7 * blend0;

  const v03 = v01 * (1 - blend1) + v23 * blend1;
  const v47 = v45 * (1 - blend1) + v67 * blend1;

  return v03 * (1 - blend2) + v47 * blend2;
}

export function GetProjectedHu(
  centerPoint: number[],
  vectorData: Float32Array,
  baseWidth: number,
  srcData: Int16Array,
  width: number,
  height: number,
  depth: number,
) {
  const centerLocation = baseWidth * 0.5;

  const fcX = centerPoint[0];
  const fcY = centerPoint[1];
  const fcZ = centerPoint[2];

  const fxX = vectorData[0];
  const fxY = vectorData[1];
  const fxZ = vectorData[2];

  const fyX = vectorData[4];
  const fyY = vectorData[5];
  const fyZ = vectorData[6];

  const xx = fcX - fxX * centerLocation - fyX * centerLocation + 0.5;
  const yy = fcY - fxY * centerLocation - fyY * centerLocation + 0.5;
  const zz = fcZ - fxZ * centerLocation - fyZ * centerLocation + 0.5;

  let resultData = new Float32Array(baseWidth * baseWidth).fill(0);

  for (let j = 0; j < baseWidth; j++) {
    for (let i = 0; i < baseWidth; i++) {
      const x = Math.floor(xx + fxX * i + fyX * j);
      const y = Math.floor(yy + fxY * i + fyY * j);
      const z = Math.floor(zz + fxZ * i + fyZ * j);
      const value = trilinear3d(
        srcData,
        width,
        height,
        depth,
        depth - z,
        height - y,
        x,
      );
      resultData[j * baseWidth + i] = value;
    }
  }

  return resultData;
}

export function GetProjectedLevel(
  centerPoint: number[],
  vectorData: Float32Array,
  baseWidth: number,
  srcData: Uint8Array,
  width: number,
  height: number,
) {
  const centerLocation = baseWidth * 0.5;

  const fcX = centerPoint[0];
  const fcY = centerPoint[1];
  const fcZ = centerPoint[2];

  const fxX = vectorData[0];
  const fxY = vectorData[1];
  const fxZ = vectorData[2];

  const fyX = vectorData[4];
  const fyY = vectorData[5];
  const fyZ = vectorData[6];

  const xx = fcX - fxX * centerLocation - fyX * centerLocation + 0.5;
  const yy = fcY - fxY * centerLocation - fyY * centerLocation + 0.5;
  const zz = fcZ - fxZ * centerLocation - fyZ * centerLocation + 0.5;

  let resultData = new Uint8Array(baseWidth * baseWidth).fill(0);

  for (let j = 0; j < baseWidth; j++) {
    for (let i = 0; i < baseWidth; i++) {
      const x = Math.floor(xx + fxX * i + fyX * j);
      const y = Math.floor(yy + fxY * i + fyY * j);
      const z = Math.floor(zz + fxZ * i + fyZ * j);
      resultData[j * baseWidth + i] = srcData[(z * height + y) * width + x];
    }
  }

  return resultData;
}

// 센터라인 vtkpolydata에서 추출한 center data는 법선벡터등이 포함된 mat4로 되어잇다.
// 이를 index 별로 구분하기 위한 함수
export function splitIntoChunks16(
  data: Float32Array,
  // vtkpolydata.getPointData().getTensors().getData() as Float32Array;
  // 이렇게 가져온 데이터를 넣어야함
): Float32Array[] {
  // return 값은 centerline index 사이즈 만큼 있고 1개 인덱스에 16개의 array가 있음
  // fx: 0~3, fy: 4~7, fz: 8~11, center point: 12~15
  // 각 4개씩있는데 마지막은 안쓰고 앞에 3개가 x, y, z 값이라 보면됨
  const chunkSize = 16;
  const numChunks = data.length / chunkSize;
  if (data.length % chunkSize !== 0) {
    throw new Error('Data length is not a multiple of 16!');
  }

  const result: Float32Array[] = [];
  for (let i = 0; i < numChunks; i++) {
    result.push(data.slice(i * chunkSize, (i + 1) * chunkSize));
  }
  return result;
}

// row data를 branch, index 별로 나누는 함수
// 첫번째 : 총 branch 갯수 (block 갯수)
// block : data 갯수 + 갯수만큼 data
export function splitArray(data: Int32Array | Float64Array): number[][] {
  const totalBlocks = data[0];
  const result: number[][] = [];
  let idx = 1;

  for (let i = 0; i < totalBlocks; i++) {
    const blockSize = data[idx++];
    const block = [];
    for (let j = 0; j < blockSize; j++) {
      block.push(data[idx++]);
    }
    result.push(block);
  }

  return result;
}

// center line 데이터를 넣으면 누적 길이 배열 함수
export function CalculateLength(
  centerpoints: number[], // x, y, z 순서로 이어져있는 1차원 데이터
  pScale: number, // planer scale
  vScale: number, // vertical scale
) {
  if (!centerpoints) return null;

  const numPoints = centerpoints.length / 3;
  const lengthArr = new Float32Array(numPoints).fill(0);

  let length = 0;

  for (let i = 0; i < numPoints - 1; i++) {
    const curx = centerpoints[i * 3];
    const cury = centerpoints[i * 3 + 1];
    const curz = centerpoints[i * 3 + 2];

    const nextx = centerpoints[(i + 1) * 3];
    const nexty = centerpoints[(i + 1) * 3 + 1];
    const nextz = centerpoints[(i + 1) * 3 + 2];

    length += Math.sqrt(
      Math.pow((nextx - curx) * pScale, 2) +
        Math.pow((nexty - cury) * pScale, 2) +
        Math.pow((nextz - curz) * vScale, 2),
    );

    lengthArr[i] = length;
  }

  lengthArr[numPoints - 1] = length;

  return lengthArr;
}

// use Marker Line function
export function getPerpendicularLine(
  targetNode: number,
  lineLength: number,
  path: { x: number; y: number }[],
) {
  if (path.length === 0 || targetNode < 0 || targetNode >= path.length)
    return {
      startX: 0,
      startY: 0,
      endX: 0,
      endY: 0,
    };
  const prevNode = path[targetNode > 0 ? targetNode - 1 : 0];
  const curNode = path[targetNode];
  const nextNode =
    path[targetNode < path.length - 1 ? targetNode + 1 : path.length - 1];

  const dx = nextNode.x - prevNode.x;
  const dy = nextNode.y - prevNode.y;
  let Nx = -dy;
  let Ny = dx;
  const len = Math.sqrt(Nx * Nx + Ny * Ny);
  if (len === 0) {
    return {
      startX: curNode.x,
      startY: curNode.y,
      endX: curNode.x,
      endY: curNode.y,
    };
  }
  Nx /= len;
  Ny /= len;
  const half = lineLength / 2;
  Nx *= half;
  Ny *= half;
  return {
    startX: curNode.x - Nx,
    startY: curNode.y - Ny,
    endX: curNode.x + Nx,
    endY: curNode.y + Ny,
  };
}

// use Marker line (SVG)
export function screenToSvgCoords(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
) {
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  return { x: svgP.x, y: svgP.y };
}

// branch label data parse
// branchcut_results_path.bin 파일읽은 함수, 읽어온거 Uint8Array로 변환후 넣기
export function parseLabelData(
  u8arr: Uint8Array,
): [number, [number, number[][]][]] {
  const view = new DataView(u8arr.buffer, u8arr.byteOffset, u8arr.byteLength);
  let offset = 0;

  if (u8arr.length < 4) return [0, []];
  const countA = view.getInt32(offset, true);
  offset += 4;

  const blocks: [number, number[][]][] = [];

  while (offset + 4 <= u8arr.length) {
    const countB = view.getInt32(offset, true);
    offset += 4;

    const items: number[][] = [];
    for (let i = 0; i < countB; ++i) {
      if (offset + 8 > u8arr.length) break;
      const val = view.getBigUint64(offset, true);

      const part1 = Number((val >> 0n) & 0xffffn);
      const part2 = Number((val >> 16n) & 0xffffn);
      const part3 = Number((val >> 32n) & 0xffffn);
      const part4 = Number((val >> 48n) & 0xffffn);

      items.push([part1, part2, part3, part4]);
      offset += 8;
    }
    blocks.push([countB, items]);
  }
  return [countA, blocks];
  // 리턴값은 첫번째countA는 브런치 갯수 (즉, 두번째 block의 갯수)
  // block : 각 index는 브런치 index
  // block 첫번째는 두번째 배열의 길이
  // block 두번째 배열 : 각 index 별 [label 값, x, y, z] 저장되어있음
}

export function parseMappingData(
  arraybuffer: ArrayBuffer,
): Record<number, number> | undefined {
  const dataView = new DataView(arraybuffer);
  let offset = 0;

  if (dataView.byteLength < 4) return;

  const blockCount = dataView.getInt32(offset, true);
  offset += 4;
  const result: Record<number, number> = {};
  for (let i = 0; i < blockCount; i++) {
    if (offset + 8 > dataView.byteLength) {
      console.log(offset);
      break;
    }
    const index = dataView.getUint32(offset, true);
    const value = dataView.getFloat32(offset + 4, true);
    result[index] = value;
    offset += 8;
  }
  return result;
}

// get branch label data from center line
// parseLabelData 이 함수를 통해 parse된 데이터에서 branch 하나의 block 데이터 넣으면 됨
// 결과는 라벨 데이터를 width, height, depth 사이즈의 1차원 데이터의 좌표에 매칭시킴
// 이 결과데이터를 vtk label image data 만들 때 씀
export function getCenterLineLabelData(
  data: [number, number[][]],
  width: number,
  height: number,
  depth: number,
) {
  const count = data[0];
  const pointdata = data[1];
  let labeldata = new Uint8Array(width * height * depth).fill(0);
  for (let i = 0; i < count; i++) {
    const [val, x, y, z] = pointdata[i];
    labeldata[z * width * height + y * width + x] = val;
  }

  return labeldata;
}

// find near branch from select 3d position
// 3차원 좌표(x, y, z) 값을 넣으면 branch id 리턴해주는 함수
export function findNearBranch(
  selectPoint: vec3, // 지정된 3차원 좌표
  centerlinePoints: vec3[],
  // ceneter line 전체 데이터를 vec3 배열로 정제해서 넣어야함
  // fcX, fcY, fcZ 를 읽어서 정제해야함
  branchIds: number[],
  // branchid_total.nii.gz 파일을 풀어서 넣어야함
  // 해당 파일은 center line 전체의 각 branch id가 매칭되는 데이터임
) {
  let branchID = 0;
  let Index = 0;
  let minDistSq = Number.MAX_VALUE;
  for (let i = 0; i < centerlinePoints.length; i++) {
    const dx = selectPoint[0] - centerlinePoints[i][0];
    const dy = selectPoint[1] - centerlinePoints[i][1];
    const dz = selectPoint[2] - centerlinePoints[i][2];
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq === 0) return [branchIds[i], i];
    if (distSq < minDistSq) {
      minDistSq = distSq;
      branchID = branchIds[i];
      Index = i;
    }
  }
  return [branchID, Index];
}

// parse graph data (area, diameter)
// area, diameter 데이터를 parse 하는 함수
// new_wall_path, new_wall_area_path, new_diameter,new_area_path bin 파일용
// BranchGraphDataset 클래스에 사용되고 있음
export function parseGraphData(buffer: Int32Array) {
  const dv = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let offset = 0;

  if (offset + 4 > buffer.byteLength) return [];
  const blockCount = dv.getInt32(offset, true);
  offset += 4;

  const result = [];

  for (let b = 0; b < blockCount; ++b) {
    if (offset + 4 > buffer.byteLength) break;

    const nDoubles = dv.getInt32(offset, true);
    offset += 4;

    const doubles = [];
    for (let i = 0; i < nDoubles; ++i) {
      if (offset + 8 > buffer.byteLength) break;
      doubles.push(dv.getFloat64(offset, true));
      offset += 8;
    }

    result.push(doubles);
  }

  return result;
}

// newCenterline_path.bin 파일을 읽어서 넣으면 branch 별 centerline index 배열이 나오는 함수
export function parseCenterLineIndex(data: Int32Array) {
  const clis = splitArray(data);
  return clis;
}

// fcx, fcy, fcz 등의 center line 데이터를 parse하는 함수
// 서버에서 읽어온거 그대로 ArrayBuffer로 넣으면 float 배열로 리턴함
// x, y, z 순서로 이어져있는 1차원 배열임
export function parseCenterPoints(data: ArrayBuffer) {
  const decompressed = isCompressed(data) ? decompress(data) : data;
  const header_data = readHeader(decompressed as ArrayBuffer);
  const imageData_data = readImage(header_data, decompressed as ArrayBuffer);
  const centerdata = new Float32Array(imageData_data);
  return centerdata;
}

// smoothing graph data value
// 그래프 표현할때 값 스무딩해서 어색하지 않게 하는 함수
function smoothingValue(
  new_centerline: number[], // center line index 배열
  curDiameter: number[], // 원본 branch diameter 데이터
  curArea: number[], // 원본 branch area 데이터
) {
  let _diameter: number[] = [];
  let _area: number[] = [];
  if (curDiameter.length === 0 || curArea.length === 0) {
    return {
      diameter: _diameter,
      area: _area,
    };
  }

  const weight = [
    0.05, 0.05, 0.1, 0.15, 0.15, 1.0, 0.15, 0.15, 0.1, 0.05, 0.05,
  ];
  const nSize = new_centerline.length;

  _diameter.length = nSize;
  _area.length = nSize;

  const nWeightCount = weight.length;
  const nSideWeightCount = Math.floor(nWeightCount / 2);

  const dataCount = nSize + nSideWeightCount * 2;

  const smoothDiaInfo = new Array<number>(dataCount);
  const smoothAreaInfo = new Array<number>(dataCount);

  for (let i = nSideWeightCount; i < nSize + nSideWeightCount; i++) {
    smoothDiaInfo[i] = curDiameter[i - nSideWeightCount];
    smoothAreaInfo[i] = curArea[i - nSideWeightCount];
  }
  for (let i = 0; i < nSideWeightCount; i++) {
    smoothDiaInfo[i] = curDiameter[0];
    smoothAreaInfo[i] = curArea[0];
  }
  for (let i = nSize + nSideWeightCount; i < dataCount; i++) {
    smoothDiaInfo[i] = curDiameter[nSize - 1];
    smoothAreaInfo[i] = curArea[nSize - 1];
  }

  for (let i = 0; i < nSize; i++) {
    let dSumDia = 0;
    let dSumArea = 0;
    for (let wg = -nSideWeightCount; wg <= nSideWeightCount; wg++) {
      if (wg === 0) continue;
      const w = weight[wg + nSideWeightCount];
      dSumDia += smoothDiaInfo[i + wg + nSideWeightCount] * w;
      dSumArea += smoothAreaInfo[i + wg + nSideWeightCount] * w;
    }
    _diameter[i] = dSumDia;
    _area[i] = dSumArea;
  }

  return {
    diameter: _diameter,
    area: _area,
  };
}

// get smoothing node index
// 그래프에 사용할 center line index를 계산하는 함수
// 원본 center line index를 사용하면 그래프가 잘안나와서 index를 스무딩 하는것
export function getNodeIndex(
  centerlineindex: number[], // 원본 branch center line index 데이터
  pScale: number,
  vScale: number,
  fcX: Float32Array,
  fcY: Float32Array,
  fcZ: Float32Array,
) {
  const zScale = Math.abs(pScale - vScale) < 0.00001 ? pScale : vScale;
  const FindTop: number[] = new Array(512000).fill(0);
  const branchsize = fcX.length;
  for (let i = 0; i < branchsize - 1; i++) {
    const curX = fcX[i];
    const curY = fcY[i];
    const curZ = fcZ[i];
    const nextX = fcX[i + 1];
    const nextY = fcY[i + 1];
    const nextZ = fcZ[i + 1];
    const fDist = Math.sqrt(
      Math.pow((curX - nextX) * pScale, 2) +
        Math.pow((curY - nextY) * pScale, 2) +
        Math.pow((curZ - nextZ) * zScale, 2),
    );
    const distindex = Math.floor(fDist * 1000);
    FindTop[distindex]++;
  }

  let nMax = Number.MIN_VALUE;
  let findmax = 0;
  for (let i = 1; i < 512000; i++) {
    if (nMax < FindTop[i]) {
      nMax = FindTop[i];
      findmax = i;
    }
  }

  const maxDensity = findmax / 1000;

  const size = centerlineindex.length;
  let curpos = 0;
  const lastpos = size - 1;
  let begin: boolean = true;
  let nodeidx: number[] = [];
  nodeidx.push(curpos);
  while (true) {
    const curindex = centerlineindex[curpos];
    const curX = fcX[curindex];
    const curY = fcY[curindex];
    const curZ = fcZ[curindex];
    let nextpos = curpos + 1;
    if (!begin) {
      while (true) {
        const nextindex = centerlineindex[nextpos];
        const nextX = fcX[nextindex];
        const nextY = fcY[nextindex];
        const nextZ = fcZ[nextindex];

        const fDist = Math.sqrt(
          Math.pow((curX - nextX) * pScale, 2) +
            Math.pow((curY - nextY) * pScale, 2) +
            Math.pow((curZ - nextZ) * zScale, 2),
        );
        if (fDist >= maxDensity) {
          nodeidx.push(nextpos);
          break;
        }
        nextpos++;
        if (nextpos >= size - 1) break;
      }
    }
    curpos = begin ? curpos : nextpos;
    if (curpos >= lastpos - 1) break;
    begin = false;
  }

  return nodeidx;
  // return 값은 집어넣은 centerlineindex 사이즈보다 작음
}

// find smoothing index (original index -> smoothing index)
// 오리지널 index를 getNodeIndex 이 함수를 통해 압축된 center line index에서 찾는 함수
export function findSmoothingIndex(
  centerlineindex: number[],
  pScale: number,
  vScale: number,
  fcX: Float32Array,
  fcY: Float32Array,
  fcZ: Float32Array,
  findindex: number,
): number {
  const zScale = Math.abs(pScale - vScale) < 0.00001 ? pScale : vScale;
  const FindTop: number[] = new Array(512000).fill(0);
  const branchsize = fcX.length;
  for (let i = 0; i < branchsize; i++) {
    const curX = fcX[i];
    const curY = fcY[i];
    const curZ = fcZ[i];
    const nextX = fcX[i];
    const nextY = fcY[i];
    const nextZ = fcZ[i];
    const fDist = Math.sqrt(
      Math.pow((curX - nextX) * pScale, 2) +
        Math.pow((curY - nextY) * pScale, 2) +
        Math.pow((curZ - nextZ) * zScale, 2),
    );
    const distindex = Math.floor(fDist * 1000);
    FindTop[distindex]++;
  }

  let nMax = Number.MIN_VALUE;
  let findmax = 0;
  for (let i = 1; i < 512000; i++) {
    if (nMax < FindTop[i]) {
      nMax = FindTop[i];
      findmax = i;
    }
  }

  const maxDensity = findmax / 1000;

  const size = centerlineindex.length;
  let curpos = 0;
  const lastpos = size - 1;
  let begin = true;
  let nodeidxIndex = 0;

  if (0 >= findindex) return 0;

  while (true) {
    const curindex = centerlineindex[curpos];
    const curX = fcX[curindex];
    const curY = fcY[curindex];
    const curZ = fcZ[curindex];
    let nextpos = curpos + 1;

    if (!begin) {
      while (true) {
        const nextindex = centerlineindex[nextpos];
        const nextX = fcX[nextindex];
        const nextY = fcY[nextindex];
        const nextZ = fcZ[nextindex];

        const fDist = Math.sqrt(
          Math.pow((curX - nextX) * pScale, 2) +
            Math.pow((curY - nextY) * pScale, 2) +
            Math.pow((curZ - nextZ) * zScale, 2),
        );
        if (fDist >= maxDensity) {
          nodeidxIndex++;
          if (nextpos >= findindex) {
            return nodeidxIndex;
          }
          break;
        }
        nextpos++;
        if (nextpos >= size - 1) break;
      }
    }
    curpos = begin ? curpos : nextpos;
    if (curpos >= lastpos - 1) break;
    begin = false;
  }

  return -1;
}

// center line 확장할때 aorta 방향으로 잡는 vector 계산 함수
export function getMassBasedNormalVector(
  src: Uint8Array,
  width: number,
  height: number,
  depth: number,
): [number, number, number] {
  const cenx = Math.floor(width / 2);
  const ceny = Math.floor(height / 2);
  const cenz = Math.floor(depth / 2);

  let total_count = 0;
  let z_plus = 0,
    z_minus = 0,
    y_plus = 0,
    y_minus = 0,
    x_plus = 0,
    x_minus = 0;

  for (let z = 0; z < depth; z++) {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (src[z * width * height + y * width + x] > 0) {
          total_count++;
          if (z < cenz) z_minus++;
          else z_plus++;
          if (y < ceny) y_minus++;
          else y_plus++;
          if (x < cenx) x_minus++;
          else x_plus++;
        }
      }
    }
  }

  if (total_count === 0) {
    return [0, 0, 0];
  }

  const z_val = (z_plus > z_minus ? z_plus : -z_minus) / total_count;
  const y_val = (y_plus > y_minus ? y_plus : -y_minus) / total_count;
  const x_val = (x_plus > x_minus ? x_plus : -x_minus) / total_count;

  const len = Math.sqrt(x_val * x_val + y_val * y_val + z_val * z_val);
  if (len === 0) {
    return [0, 0, 0];
  }
  return [x_val / len, y_val / len, z_val / len];
}

// expand center line data
// center line 확장하는 함수
export function expandCenterline(
  cenData: Float32Array, //  center line x, y, z 순으로 이어진 row data 넣으면 됨
  Aorta: Uint8Array, // width * height * depth사이즈의 aorta row data
  width: number,
  height: number,
  frontexpand: number = 50,
  backexpand: number = 50,
  rate: number = 1,
) {
  if (!cenData || !Aorta) return null;

  // find aorta
  const checksize = 20;
  let aortaData = new Uint8Array(checksize * checksize * checksize * 8).fill(0);

  let exist_aorta = false;
  for (let z = -checksize; z < checksize; z++) {
    for (let y = -checksize; y < checksize; y++) {
      for (let x = -checksize; x < checksize; x++) {
        const xtemp = Math.floor(cenData[0] * rate) + x;
        const ytemp = Math.floor(cenData[1] * rate) + y;
        const ztemp = Math.floor(cenData[2] * rate) + z;
        const index = ztemp * width * height + ytemp * width + xtemp;

        if (Aorta[index] > 0) {
          exist_aorta = true;
          aortaData[
            (z + checksize) * (checksize * 2) * (checksize * 2) +
              (y + checksize) * (checksize * 2) +
              (x + checksize)
          ] = 255;
        }
      }
    }
  }

  frontexpand = exist_aorta ? frontexpand : 0;
  const cenlength = cenData.length;
  const cencount = cenlength / 3;
  const startvec = getMassBasedNormalVector(
    aortaData,
    checksize * 2,
    checksize * 2,
    checksize * 2,
  );
  const endvec = [
    cenData[(cencount - 1) * 3] - cenData[(cencount - 2) * 3],
    cenData[(cencount - 1) * 3 + 1] - cenData[(cencount - 2) * 3 + 1],
    cenData[(cencount - 1) * 3 + 2] - cenData[(cencount - 2) * 3 + 2],
  ] as vec3;
  vec3.normalize(endvec, endvec);

  let newcendata = new Float32Array(cenlength + (frontexpand + backexpand) * 3);
  for (let i = 0; i < frontexpand; i++) {
    newcendata[i * 3] = cenData[0] + startvec[0] * (frontexpand - i);
    newcendata[i * 3 + 1] = cenData[1] + startvec[1] * (frontexpand - i);
    newcendata[i * 3 + 2] = cenData[2] + startvec[2] * (frontexpand - i);
  }
  for (let i = frontexpand; i < cencount + frontexpand; i++) {
    newcendata[i * 3] = cenData[(i - frontexpand) * 3];
    newcendata[i * 3 + 1] = cenData[(i - frontexpand) * 3 + 1];
    newcendata[i * 3 + 2] = cenData[(i - frontexpand) * 3 + 2];
  }
  for (let i = 0; i < backexpand; i++) {
    newcendata[(cencount + frontexpand + i) * 3] =
      cenData[(cencount - 1) * 3] + endvec[0] * (i + 1);
    newcendata[(cencount + frontexpand + i) * 3 + 1] =
      cenData[(cencount - 1) * 3 + 1] + endvec[1] * (i + 1);
    newcendata[(cencount + frontexpand + i) * 3 + 2] =
      cenData[(cencount - 1) * 3 + 2] + endvec[2] * (i + 1);
  }
  return newcendata;
}

// ---- Plaque Dataset 관련 타입/상수 ----
export const PLAQUE_LABELS = [
  'lumen',
  'vessel',
  'plaque',
  'calcified',
  'noncalcified',
  'lowattenuation',
] as const;

type PlaqueLabel = (typeof PLAQUE_LABELS)[number];

type PlaqueFAIData = {
  [K in `${PlaqueLabel}_count`]: number[];
} & {
  [K in `${PlaqueLabel}_volume`]: number[];
} & {
  fai: number[][];
  length: number[];
};

type PlaqueInfo = { counts: number[]; volumes: number[] };
type NodeInfo = { plaque: PlaqueInfo; fai: number[]; length: number };

type Stats = {
  min: number;
  max: number;
  mean: number;
};

export const BranchIDType = {
  RCA: 0,
  LAD: 1,
  LCX: 2,
  LM: 3,
};

type AnalysisResult = {
  lumenArea: Float32Array;
  wallArea: Float32Array;
  lumenDiameter: Float32Array;
  wallDiameter: Float32Array;
  hu: Int16Array;
  length?: Float32Array;
  stats: {
    lumenArea: Stats;
    wallArea: Stats;
    lumenDiameter: Stats;
    wallDiameter: Stats;
    hu: Stats;
    totalLength?: number;
  };
};

// ---- PlaqueDataset의 branchInfo arrayBuffer 읽기 함수 ----
function readBranchInfo(arrayBuffer: ArrayBuffer): NodeInfo[][] {
  const view = new DataView(arrayBuffer);
  let offset = 0;

  function readInt(): number {
    const value = view.getInt32(offset, true);
    offset += 4;
    return value;
  }
  function readFloat(): number {
    const value = view.getFloat32(offset, true);
    offset += 4;
    return value;
  }
  function readIntArray(length: number): number[] {
    const arr: number[] = [];
    for (let i = 0; i < length; i++) {
      arr.push(readInt());
    }
    return arr;
  }
  function readFloatArray(length: number): number[] {
    const arr: number[] = [];
    for (let i = 0; i < length; i++) {
      arr.push(readFloat());
    }
    return arr;
  }
  function readShort(): number {
    const value = view.getInt16(offset, true);
    offset += 2;
    return value;
  }
  function readShortArray(length: number): number[] {
    const arr: number[] = [];
    for (let i = 0; i < length; i++) {
      arr.push(readShort());
    }
    return arr;
  }

  const branchCount = readInt();
  const result: NodeInfo[][] = [];
  for (let i = 0; i < branchCount; i++) {
    const nodeCount = readInt();
    const branch: NodeInfo[] = [];
    for (let node = 0; node < nodeCount; node++) {
      const counts_size = readInt();
      const counts = readIntArray(counts_size);

      const volumes_size = readInt();
      const volumes = readFloatArray(volumes_size);

      const fai_size = readInt();
      const fai = readShortArray(fai_size);

      const length = readFloat();

      branch.push({ plaque: { counts, volumes }, fai, length });
    }
    result.push(branch);
  }
  return result;
}

export class BranchInfo {
  private PlaqueFaiData: PlaqueFAIData[] = [];
  private graphResults: Map<number, AnalysisResult> = new Map();
  private oriType: Int32Array = new Int32Array();
  private branchindex: number[][] = [];
  private originBranchId: number[] = [];

  constructor(
    plaqueArrayBuffer: ArrayBuffer,
    lumenArea: Int32Array,
    lumenDiameter: Int32Array,
    wallArea: Int32Array,
    wallDiameter: Int32Array,
    centerLineIndex: Int32Array,
    fcX: Float32Array,
    fcY: Float32Array,
    fcZ: Float32Array,
    HUvtkImageData: any,
    typetotal: Int32Array,
    private half: boolean,
    private pScale: number = 1,
    private vScale: number = 1,
    private faimin: number = -190,
    private faimax: number = -30,
  ) {
    this.load(
      plaqueArrayBuffer,
      lumenArea,
      lumenDiameter,
      wallArea,
      wallDiameter,
      centerLineIndex,
      fcX,
      fcY,
      fcZ,
      HUvtkImageData,
      typetotal,
    );
  }

  // =================== Plaque (label, FAI 등) ===================

  public load(
    arrayBuffer: ArrayBuffer,
    lumenArea: Int32Array,
    lumenDiameter: Int32Array,
    wallArea: Int32Array,
    wallDiameter: Int32Array,
    centerLineIndex: Int32Array,
    fcX: Float32Array,
    fcY: Float32Array,
    fcZ: Float32Array,
    HUvtkImageData: any,
    typetotal: Int32Array,
  ) {
    if (arrayBuffer) {
      const nodeInfos: NodeInfo[][] = readBranchInfo(arrayBuffer);

      this.PlaqueFaiData = nodeInfos.map((branchNodes) => {
        const counts: Record<PlaqueLabel, number[]> = Object.fromEntries(
          PLAQUE_LABELS.map((label) => [label, []]),
        ) as unknown as Record<PlaqueLabel, number[]>;
        const volumes: Record<PlaqueLabel, number[]> = Object.fromEntries(
          PLAQUE_LABELS.map((label) => [label, []]),
        ) as unknown as Record<PlaqueLabel, number[]>;
        const fai: number[][] = [];
        const length: number[] = [];
        for (const node of branchNodes) {
          PLAQUE_LABELS.forEach((label, idx) => {
            counts[label].push(node.plaque.counts[idx] ?? 0);
            volumes[label].push(node.plaque.volumes[idx] ?? 0);
          });
          fai.push(node.fai);
          length.push(node.length);
        }
        const branchObj: any = {};
        PLAQUE_LABELS.forEach((label) => {
          branchObj[`${label}_count`] = counts[label];
          branchObj[`${label}_volume`] = volumes[label];
        });
        branchObj.fai = fai;
        branchObj.length = length;
        return branchObj as PlaqueFAIData;
      });
    }

    if (
      lumenArea &&
      lumenDiameter &&
      wallArea &&
      wallDiameter &&
      centerLineIndex &&
      fcX &&
      fcY &&
      fcZ &&
      HUvtkImageData
    ) {
      const las = parseGraphData(lumenArea);
      const was = parseGraphData(wallArea);
      const lds = parseGraphData(lumenDiameter);
      const wds = parseGraphData(wallDiameter);
      const clis = parseCenterLineIndex(centerLineIndex);
      this.branchindex = clis;

      const numBranches = las.length;
      this.originBranchId = new Array<number>(numBranches);

      const scale = this.half ? 0.5 : 1;
      for (let branchID = 0; branchID < numBranches; branchID++) {
        const lumenareaArray = las[branchID];
        const wallareaArray = was[branchID];
        const lumendiameterArray = lds[branchID];
        const walldiameterArray = wds[branchID];
        const centerlineindex = clis[branchID];

        const newlumendia = lumendiameterArray;
        const newlumenarea = lumenareaArray;
        // const { diameter: newlumendia, area: newlumenarea } = smoothingValue(
        //   centerlineindex,
        //   lumendiameterArray,
        //   lumenareaArray,
        // );
        const newwalldia = walldiameterArray;
        const newwallarea = wallareaArray;
        // const { diameter: newwalldia, area: newwallarea } = smoothingValue(
        //   centerlineindex,
        //   walldiameterArray,
        //   wallareaArray,
        // );

        const cenX = fcX;
        const cenY = fcY;
        const cenZ = fcZ;

        let AreaMin = Number.MAX_VALUE,
          AreaMax = Number.MIN_VALUE;
        let AreaWallMin = Number.MAX_VALUE,
          AreaWallMax = Number.MIN_VALUE;
        let DiameterMin = Number.MAX_VALUE,
          DiameterMax = Number.MIN_VALUE;
        let DiameterWallMin = Number.MAX_VALUE,
          DiameterWallMax = Number.MIN_VALUE;
        let HuMin = Number.MAX_VALUE,
          HuMax = Number.MIN_VALUE;

        // 일단 node 스무딩 안하기
        //const nodeindex = getNodeIndex(
        //  centerlineindex,
        //  this.pScale,
        //  this.vScale,
        //  cenX,
        //  cenY,
        //  cenZ,
        //);
        const nodeindex = centerlineindex;
        const n = nodeindex.length;
        //const lumenAreaArr = new Float32Array(n);
        //const wallAreaArr = new Float32Array(n);
        //const lumenDiameterArr = new Float32Array(n);
        //const wallDiameterArr = new Float32Array(n);
        const lumenAreaArr = new Float32Array(newlumenarea);
        const wallAreaArr = new Float32Array(newwallarea);
        const lumenDiameterArr = new Float32Array(newlumendia);
        const wallDiameterArr = new Float32Array(newwalldia);
        const huArr = new Int16Array(n);

        for (let i = 0; i < n; i++) {
          const idx = nodeindex[i];

          // if (
          //   newlumenarea[i] === 0 ||
          //   newwallarea[i] === 0 ||
          //   newlumendia[i] === 0 ||
          //   newwalldia[i] === 0
          // ) {
          //   continue;
          // }

          const world = HUvtkImageData!.indexToWorld([
            cenX[i] * scale,
            cenY[i] * scale,
            cenZ[i] * scale,
          ]);
          // const HUValue = HUvtkImageData!.getScalarValueFromWorld([
          //   cenX[i],
          //   cenY[i],
          //   cenZ[i],
          // ]);

          let HUValue = 0;
          const scalars = HUvtkImageData.getPointData().getScalars();
          if (scalars && typeof scalars.getComponent === 'function') {
            HUValue = HUvtkImageData.getScalarValueFromWorld(world);
          }

          //lumenAreaArr[i] = newlumenarea[i];
          //wallAreaArr[i] = newwallarea[i];
          //lumenDiameterArr[i] = newlumendia[i];
          //wallDiameterArr[i] = newwalldia[i];
          huArr[i] = HUValue;

          AreaMin = Math.min(AreaMin, newlumenarea[i]);
          AreaMax = Math.max(AreaMax, newlumenarea[i]);

          AreaWallMin = Math.min(AreaWallMin, newwallarea[i]);
          AreaWallMax = Math.max(AreaWallMax, newwallarea[i]);

          DiameterMin = Math.min(DiameterMin, newlumendia[i]);
          DiameterMax = Math.max(DiameterMax, newlumendia[i]);

          DiameterWallMin = Math.min(DiameterWallMin, walldiameterArray[idx]);
          DiameterWallMax = Math.max(DiameterWallMax, walldiameterArray[idx]);

          HuMin = Math.min(HuMin, HUValue);
          HuMax = Math.max(HuMax, HUValue);

          if (i === n - 1) {
            this.originBranchId[branchID] = idx;
          }
        }

        function meanRange(arr: Float32Array | Int16Array): number {
          let sum = 0;
          for (let i = 0; i < arr.length; i++) {
            sum += arr[i];
          }
          return sum / arr.length;
        }
        function replaceZero(arr: Float32Array, value: number) {
          for (let i = 0; i < arr.length; i++) {
            if (arr[i] === 0) arr[i] = value;
          }
        }
        const area_mean = meanRange(lumenAreaArr);
        const wallarea_mean = meanRange(wallAreaArr);
        const diameter_mean = meanRange(lumenDiameterArr);
        const walldiameter_mean = meanRange(wallDiameterArr);
        const hu_mean = meanRange(huArr);
        replaceZero(lumenAreaArr, area_mean);
        replaceZero(wallAreaArr, wallarea_mean);
        replaceZero(lumenDiameterArr, diameter_mean);
        replaceZero(wallDiameterArr, walldiameter_mean);

        const result: AnalysisResult = {
          lumenArea: lumenAreaArr,
          wallArea: wallAreaArr,
          lumenDiameter: lumenDiameterArr,
          wallDiameter: wallDiameterArr,
          hu: huArr,
          stats: {
            lumenArea: { min: AreaMin, max: AreaMax, mean: area_mean },
            wallArea: {
              min: AreaWallMin,
              max: AreaWallMax,
              mean: wallarea_mean,
            },
            lumenDiameter: {
              min: DiameterMin,
              max: DiameterMax,
              mean: diameter_mean,
            },
            wallDiameter: {
              min: DiameterWallMin,
              max: DiameterWallMax,
              mean: walldiameter_mean,
            },
            hu: { min: HuMin, max: HuMax, mean: hu_mean },
          },
        };

        this.graphResults.set(branchID, result);
      }
      if (typetotal) {
        this.oriType = typetotal;
      }
    }
  }

  public getOriginBranchId(branchID: number) {
    if (this.originBranchId.length <= branchID || branchID < 0) {
      return {
        id: -1,
        name: '',
      };
    }
    const names = ['RCA', 'LAD', 'LCX', 'LM'];
    const ids = this.oriType[this.originBranchId[branchID]];
    return {
      id: ids,
      name: names[ids],
    };
  }

  public getLMCount() {
    const count = (arr: Int32Array, value: number) =>
      arr.reduce((acc, x) => acc + (x === value ? 1 : 0), 0);
    return count(this.oriType, 3);
  }

  // 브런치 갯수 함수
  public getBranchCount(): number {
    return this.PlaqueFaiData.length;
  }

  // plaque의 count, volume / fai의 hu / length 데이터 다 들어있는 값 리턴 함수
  public getPlaqueFAIData(branchID: number): PlaqueFAIData | undefined {
    return this.PlaqueFaiData[branchID];
  }

  // 원하는 plaque 라벨의 count array 리턴 함수
  public getLabelCounts(
    branchID: number,
    label: PlaqueLabel,
  ): number[] | undefined {
    const branch = this.getPlaqueFAIData(branchID);
    return branch ? branch[`${label}_count`] : undefined;
  }

  // 원하는 plaque 라벨의 volume array 리턴 함수
  public getLabelVolumes(
    branchID: number,
    label: PlaqueLabel,
  ): number[] | undefined {
    const branch = this.getPlaqueFAIData(branchID);
    return branch ? branch[`${label}_volume`] : undefined;
  }

  // 해당 branch의 fai hu array 리턴함수
  // 2차원 배열로 index 별로 각 index 단면의 fai hu 배열이 들어있음
  public getFaiArray(branchID: number): number[][] | undefined {
    return this.getPlaqueFAIData(branchID)?.fai;
  }

  // 해당 branch의 length array 리턴 함수
  public getLengthArray(branchID: number): number[] | undefined {
    return this.getPlaqueFAIData(branchID)?.length;
  }

  // 해당 위치의 길이 리턴 함수
  public getLengthValue(branchID: number, index: number): number {
    const arr = this.getLengthArray(branchID);
    return arr && index >= 0 && index < arr.length ? arr[Math.floor(index)] : 0;
  }

  // 해당 branch의 실제 길이에 해당하는 index 리턴 함수
  // 길이는 mm 단위로 계산되어있음
  public getIndexofLength(branchID: number, length: number) {
    const lengthdata = this.getLengthArray(branchID);
    if (!lengthdata) return 0;
    let index = 0;
    for (const len of lengthdata) {
      if (length <= len) {
        break;
      }
      index++;
    }
    return index;
  }

  // proximal, distal 지정하면 거기에 해당 하는 plaque 정보 return 하는 함수
  // proximal, distal 지정안하면 처음부터 끝까지 계산함
  public getPlaqueTotalInfo(
    branchIdx: number,
    proximal?: number,
    stenosis?: number,
    distal?: number,
  ) {
    const branchdata = this.getPlaqueFAIData(branchIdx);
    const lengthdata = this.getLengthArray(branchIdx);
    const lumearea = this.getLumenArea(branchIdx);
    const lumendiameter = this.getLumenDiameter(branchIdx);
    const wallarea = this.getWallArea(branchIdx);
    if (!branchdata || !lengthdata || !lumearea || !lumendiameter || !wallarea)
      return null;
    const length = lumearea.length;

    const prox = proximal !== undefined ? proximal : 0;
    const dist = distal !== undefined ? distal : length - 1;
    const mid = stenosis !== undefined ? stenosis : (prox + dist) / 2;

    function sumRange(arr: number[]): number {
      return arr.slice(prox, dist + 1).reduce((a, b) => a + b, 0);
    }
    function minRange(arr: Float32Array): number {
      return Math.min(...arr.slice(prox, dist + 1));
    }
    function meanRange(arr: Float32Array, start: number, end: number): number {
      let sum = 0;
      for (let i = start; i < end; i++) {
        sum += arr[i];
      }
      return sum / (end - start);
    }
    const calcRate = (arr: Float32Array) => {
      const prox_len = this.getLengthValue(branchIdx, prox);
      const mid_len = this.getLengthValue(branchIdx, mid);
      const dist_len = this.getLengthValue(branchIdx, dist);

      const prox_inter = this.getIndexofLength(branchIdx, prox_len + 1);
      const mid_inter_before = this.getIndexofLength(branchIdx, mid_len - 0.75);
      const mid_inter_after = this.getIndexofLength(branchIdx, mid_len + 0.75);
      const dist_inter = this.getIndexofLength(branchIdx, dist_len - 1);

      const proxmean = meanRange(arr, prox, prox_inter);
      const midmean = meanRange(arr, mid_inter_before, mid_inter_after);
      const distmean = meanRange(arr, dist_inter, dist);

      return (midmean * 2) / (proxmean + distmean);
    };

    let vesselValue = sumRange(branchdata.vessel_volume);
    let plaqueValue = sumRange(branchdata.plaque_volume);

    return {
      lumenValue: sumRange(branchdata.lumen_volume),
      vesselValue,
      plaqueValue,
      calcifiedValue: sumRange(branchdata.calcified_volume),
      noncalcifiedValue: sumRange(branchdata.noncalcified_volume),
      lowattenuationValue: sumRange(branchdata.lowattenuation_volume),
      lengthValue: lengthdata[dist] - lengthdata[prox],
      plaqueburden: vesselValue !== 0 ? plaqueValue / vesselValue : 0,
      minLumenArea: minRange(lumearea),
      minLumenDiameter: minRange(lumendiameter),
      remodelingIndex: calcRate(wallarea),
      stenosisDegree: calcRate(lumearea),
    };
  }

  // proximal, distal 지정하면 거기에 해당 하는 fai 정보 return 하는 함수
  // proximal, distal 지정안하면 처음부터 끝까지 계산함
  public getFAITotalInfo(
    branchIdx: number,
    proximal?: number,
    distal?: number,
  ) {
    const branchdata = this.getPlaqueFAIData(branchIdx);
    if (!branchdata) return null;

    const prox = proximal !== undefined ? proximal : 0;
    const dist =
      distal !== undefined ? distal : branchdata.lumen_volume.length - 1;

    function meanRange2D(arr: number[][]): number {
      const values = arr.slice(prox, dist + 1).flat();
      const n = values.length;
      if (n === 0) return 0;
      const mean = values.reduce((a, b) => a + b, 0) / n;
      return mean;
    }
    const mean = meanRange2D(branchdata.fai);

    function sdRange2D(arr: number[][]): number {
      const values = arr.slice(prox, dist + 1).flat();
      const n = values.length;
      if (n === 0) return 0;
      // const mean = meanRange2D(arr);
      const variance =
        values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / n;
      return Math.sqrt(variance);
    }

    const sd = sdRange2D(branchdata.fai);

    return {
      faimean: mean,
      faisd: sd,
    };
  }

  // proximal, distal 지정하면 거기에 해당 하는 fai count, volume return 하는 함수
  // proximal, distal 지정안하면 처음부터 끝까지 계산함
  // fai 그래프에 사용하기 위한 함수
  public getFAIHUCountAndVolume(
    branchIdx: number,
    proximal?: number,
    distal?: number,
  ) {
    const branchdata = this.getPlaqueFAIData(branchIdx);
    if (!branchdata) return null;

    const prox = proximal !== undefined ? proximal : 0;
    const dist =
      distal !== undefined ? distal : branchdata.lumen_volume.length - 1;

    const countArray = new Array(this.faimax - this.faimin + 1).fill(0);
    const volumeArray = new Array(this.faimax - this.faimin + 1).fill(0);

    const values = branchdata.fai.slice(prox, dist + 1).flat();
    const n = values.length;
    if (n === 0) return null;

    for (const value of values) {
      if (value < this.faimin || value > this.faimax) continue;
      const idx = value - this.faimin;
      countArray[idx]++;
    }

    let index = 0;
    for (const count of countArray) {
      volumeArray[index] = count * this.pScale * this.pScale * this.vScale;
      index++;
    }

    return {
      countArray: countArray,
      volumeArray: volumeArray,
    };
    // 각 hu 별 count 한 array 리턴, 이 클래스에서 선언한 faimin 값 index에 맞춰서 사용하면됨
    // volume은 count array에 1:1 대응되게 계산한것이라 동일하게 사용하면 됨
  }

  // cross section에서 해당 단면의 plaque 정보 표시하기 위한 함수
  // branch id와 slice index 넣으면 해당 단면의 plaque 벼로 persent 값 나옴
  // 값은 소수값이니 표기할땐 100 곱해야한다
  public getSlicePlaquePersent(branchIdx: number, sliceIdx: number) {
    const branchdata = this.getPlaqueFAIData(branchIdx);
    if (!branchdata) return null;

    const rcpTotal = branchdata.vessel_count[sliceIdx]
      ? 1 / branchdata.vessel_count[sliceIdx]
      : 0;

    return {
      lumenPersent: branchdata.lumen_count[sliceIdx] * rcpTotal,
      calcifiedPersent: branchdata.calcified_count[sliceIdx] * rcpTotal,
      noncalcifiedPersent: branchdata.noncalcified_count[sliceIdx] * rcpTotal,
      lowattenuationPersent:
        branchdata.lowattenuation_count[sliceIdx] * rcpTotal,
    };
  }

  public getSlicePlaqueCount(branchIdx: number, sliceIdx: number) {
    const branchdata = this.getPlaqueFAIData(branchIdx);
    if (!branchdata) return null;
    return {
      lumenCount: branchdata.lumen_count[sliceIdx],
      calcifiedCount: branchdata.calcified_count[sliceIdx],
      noncalcifiedCount: branchdata.noncalcified_count[sliceIdx],
      lowattenuationCount: branchdata.lowattenuation_count[sliceIdx],
    };
  }

  public getResult(branchID: number): AnalysisResult | null {
    return this.graphResults.get(branchID) || null;
  }

  public getStats(branchID: number) {
    return this.graphResults.get(branchID)?.stats || null;
  }

  public getLumenArea(branchID: number) {
    return this.graphResults.get(branchID)?.lumenArea || null;
  }

  public getWallArea(branchID: number) {
    return this.graphResults.get(branchID)?.wallArea || null;
  }

  public getLumenDiameter(branchID: number) {
    return this.graphResults.get(branchID)?.lumenDiameter || null;
  }

  public getWallDiameter(branchID: number) {
    return this.graphResults.get(branchID)?.wallDiameter || null;
  }

  public getHUArray(branchID: number) {
    return this.graphResults.get(branchID)?.hu || null;
  }

  public getLumenAreaValue(branchID: number, index: number): number | null {
    const arr = this.graphResults.get(branchID)?.lumenArea;
    return arr && index >= 0 && index < arr.length ? arr[index] : null;
  }

  public getWallAreaValue(branchID: number, index: number): number | null {
    const arr = this.graphResults.get(branchID)?.wallArea;
    return arr && index >= 0 && index < arr.length ? arr[index] : null;
  }

  public getLumenDiameterValue(branchID: number, index: number): number | null {
    const arr = this.graphResults.get(branchID)?.lumenDiameter;
    return arr && index >= 0 && index < arr.length ? arr[index] : null;
  }

  public getWallDiameterValue(branchID: number, index: number): number | null {
    const arr = this.graphResults.get(branchID)?.wallDiameter;
    return arr && index >= 0 && index < arr.length ? arr[index] : null;
  }

  public getHUValue(branchID: number, index: number): number | null {
    const arr = this.graphResults.get(branchID)?.hu;
    return arr && index >= 0 && index < arr.length ? arr[index] : null;
  }

  public getFAIIndex(
    rcaBranchID: number,
    ladBranchID: number,
    lcxBranchID: number,
  ) {
    const lad = this.branchindex[ladBranchID];
    const lcx = this.branchindex[lcxBranchID];

    const getStartEndIndex = (
      index: number[],
      type: number,
      branchid: number,
    ) => {
      let firlength = -1;
      let s = 0;
      let e = index.length - 1;
      for (let i = 0; i < index.length; i++) {
        if (this.oriType[index[i]] === type) {
          const length = this.getLengthValue(branchid, i);
          if (firlength === -1) {
            firlength = length;
            s = i;
          }

          if (length - firlength >= 40) {
            e = i;
            break;
          }
        }
      }
      return {
        start: s,
        end: e,
      };
    };

    const ladindex = getStartEndIndex(lad, BranchIDType.LAD, ladBranchID);
    const lcxindex = getStartEndIndex(lcx, BranchIDType.LCX, lcxBranchID);

    return {
      RCA: [
        this.getIndexofLength(rcaBranchID, 10),
        this.getIndexofLength(rcaBranchID, 50),
      ],
      LAD: [ladindex.start, ladindex.end],
      LCX: [lcxindex.start, lcxindex.end],
    };
  }

  public getBranchIndex() {
    return this.branchindex;
  }

  public findSelectBranchIndex(branchid: number, total_index: number) {
    const branch = this.branchindex[branchid];
    const index = branch.indexOf(total_index);
    return index;
  }

  public getPlaqueArea(branchid: number) {
    const lumenarr = this.getLumenArea(branchid);
    const wallarr = this.getWallArea(branchid);
    const plaqueinfo = this.getPlaqueFAIData(branchid);
    if (!lumenarr || !wallarr || !plaqueinfo) {
      return {
        low: [],
        noncalc: [],
        calc: [],
      }
    }
    let lowarr = new Array<number>(wallarr.length);
    let nonarr = new Array<number>(wallarr.length);
    let calarr = new Array<number>(wallarr.length);

    for (let i = 0; i < wallarr.length; i++) {
      const lc = plaqueinfo!.lowattenuation_count[i];
      const nc = plaqueinfo!.noncalcified_count[i];
      const cc = plaqueinfo!.calcified_count[i];
      const plaquetotalcount = lc + nc + cc;
      const wallarea = wallarr[i] - lumenarr![i];
      lowarr[i] = wallarea * (lc / plaquetotalcount);
      nonarr[i] = wallarea * (nc / plaquetotalcount);
      calarr[i] = wallarea * (cc / plaquetotalcount);
    }

    return {
      low: lowarr,
      noncalc: nonarr,
      calc: calarr,
    };
  }

  public getStenosisMid(branchid: number, proximal: number, distal: number) {
    const len = Math.abs(proximal - distal);
    let mid = (proximal + distal) / 2;
    const area = this.getLumenArea(branchid);
    if (area) {
      let minarea = Number.MAX_VALUE;
      for (let i = 0; i < len; i++) {
        if (minarea > area[proximal + i]) {
          minarea = area[proximal + i];
          mid = proximal + i;
        }
      }
    }

    return mid;
  }
}

export function branch_type_to_id(
  branchIDArray: Int32Array,
  typeArray: Int32Array,
) {
  const length = typeArray.length;
  const typemin = Math.min(...typeArray);
  const typemax = Math.max(...typeArray);
  const typecount = typemax - typemin + 1;
  let arr = new Array(typecount).fill(-1);

  for (let i = 0; i < length; i++) {
    if (arr[typeArray[i]] == -1) {
      arr[typeArray[i]] = branchIDArray[i];
    }
  }
  return arr;
}

export type SplitOptions = {
  trim?: boolean;
  keepEmpty?: boolean;
  collapseSpaces?: boolean;
};

export function splitNarrativeLines(raw: string, opts: SplitOptions = {}) {
  const { trim = true, keepEmpty = true, collapseSpaces = false } = opts;

  if (!raw) return [];

  const normalized = raw.replace(/\r\n?/g, '\n').replace(/\\n/g, '\n');

  let parts = normalized.split('\n');
  parts = parts.map((line) => {
    let s = line;
    if (trim) s = s.trim();
    if (collapseSpaces) s = s.replace(/[ \t]+/g, ' ');
    return s;
  });

  if (!keepEmpty) {
    parts = parts.filter((s) => s.length > 0);
  }

  return parts;
}

export function numbersByOrder(
  pairs: [string, number][],
  order: string[],
  { missing = 'skip' }: { missing?: 'skip' | 'undefined' | 'throw' } = {},
): number[] {
  // 같은 키가 여러 번 있으면 마지막 값이 남습니다(원하면 첫 값만 쓰도록 바꿀 수 있음)
  const map = new Map<string, number>();
  for (const [k, v] of pairs) map.set(k, v);

  if (missing === 'skip') {
    return order.flatMap((k) => (map.has(k) ? [map.get(k)!] : []));
  }
  if (missing === 'undefined') {
    // (number | undefined)[] 원한다면 반환 타입을 바꾸세요
    return order.map((k) => map.get(k) as any);
  }
  // missing === "throw"
  return order.map((k) => {
    if (!map.has(k)) throw new Error(`Key not found: ${k}`);
    return map.get(k)!;
  });
}

export function calcStenosisText(
  paramData: CalciumAnalysisResult,
  lesioninfo: LesionInfo[][],
  branchinfo: BranchInfo,
) {
  if (!paramData || !branchinfo) return '';
  const name = paramData.SavedInfo.Coronary.newType;
  let stenosistext = '';
  if (paramData.SavedInfo.Coronary.auto_diagnostic_report !== undefined) {
    stenosistext = paramData.SavedInfo.Coronary.auto_diagnostic_report;
  } else {
    const main = ['RCA', 'LAD', 'LCX', 'LM'];
    let rcatext = '';
    const newtypelength = name === null ? 0 : name.length;
    for (let i = 0; i < newtypelength; i++) {
      const [branch, index] = name[i];
      if (index === -1) continue;
      if (!main.includes(branch)) continue;
      const lesion = lesioninfo[index];
      if(!lesion) continue;
      if (lesion.length === 0) {
        if (branch === 'RCA') rcatext += branch + ' unremarkable' + '\r\n';
        else stenosistext += branch + ' unremarkable' + '\r\n';
        continue;
      }
      if (branch === 'LM') {
        rcatext += branch + ' unremarkable' + '\r\n';
        continue;
      }
      for (let k = 0; k < lesion.length; k++) {
        const [start, mid, end, persent, plaque] = lesion[k];
        let plq =
          plaque === 0
            ? ' mixied plaque'
            : plaque === 1
              ? ' calcified plaque'
              : plaque === 2
                ? ' noncalcified plaque'
                : '';
        let val =
          persent === 0
            ? ' with 0%'
            : persent < 0.25
              ? ' with 1-24%'
              : persent < 0.5
                ? ' with 25-49%'
                : persent < 0.7
                  ? ' with 50-69%'
                  : ' with ≥70%';
        let pos = ' proximal';
        const arr = branchinfo.getLengthArray(index);
        if (arr) {
          const totallength = arr[arr?.length - 1];
          const curlength = branchinfo.getLengthValue(
            index,
            Math.floor((start + end) * 0.5),
          );
          if (branch === 'LCX') {
            const inter = Math.floor(totallength / 2);
            if (curlength < inter) {
              pos = ' proximal';
            } else {
              pos = ' distal';
            }
          } else {
            const inter = Math.floor(totallength / 3);
            if (curlength < inter) {
              pos = ' proximal';
            } else if (curlength < inter * 2) {
              pos = ' mid';
            } else {
              pos = ' distal';
            }
          }
        }

        if (branch === 'RCA') rcatext += branch + pos + plq + val + '\r\n';
        else if (branch === 'LM') rcatext += branch + pos + plq + val + '\r\n';
        else stenosistext += branch + pos + plq + val + '\r\n';
      }
    }
    stenosistext = rcatext + stenosistext;
    let aga = paramData.SavedInfo.Coronary.AgatstonScore[0];
    aga = Math.floor(aga * 10) / 10;
    stenosistext += '\r\n' + 'Calcium Scoring = ' + String(aga);
  }

  return stenosistext;
}
